<template>
  <a-drawer
    :visible="visible"
    :width="480"
    :closable="true"
    :destroy-on-close="true"
    :mask-closable="false"
    wrap-class-name="itineraries-create-drawer"
    @close="onClose"
  >
    <div slot="title" class="itineraries-create-drawer__header">
      <h3 class="itineraries-create-drawer__title">{{ drawerTitle }}</h3>
      <span class="itineraries-create-drawer__status">
        {{ isEdit ? "Editar paradas" : "Em rascunho" }}
      </span>
    </div>

    <a-steps
      v-if="!isEdit"
      :current="currentStep"
      size="small"
      class="itineraries-create-drawer__steps"
    >
      <a-step>
        <template slot="title">
          <a
            class="itineraries-create-drawer__step-link"
            href="#"
            @click.prevent="goToStep(0)"
          >Nome</a>
        </template>
      </a-step>
      <a-step :disabled="!canReachStops">
        <template slot="title">
          <a
            class="itineraries-create-drawer__step-link"
            href="#"
            :class="{ 'is-disabled': !canReachStops }"
            @click.prevent="goToStep(1)"
          >Paradas</a>
        </template>
      </a-step>
      <a-step :disabled="!canReachReview">
        <template slot="title">
          <a
            class="itineraries-create-drawer__step-link"
            href="#"
            :class="{ 'is-disabled': !canReachReview }"
            @click.prevent="goToStep(2)"
          >Revisar e enviar</a>
        </template>
      </a-step>
    </a-steps>

    <div class="itineraries-create-drawer__body">
      <ItineraryNameStep
        v-if="currentStep === 0"
        :codigo="form.codigo"
        :nome.sync="form.nome"
        :descricao.sync="form.descricao"
      />

      <ItineraryStopsStep
        v-else-if="currentStep === 1"
        :paradas="form.paradas"
        :itinerary-id="form.id"
        @change="onParadasChange"
      />

      <ItineraryReviewStep
        v-else
        :codigo="form.codigo"
        :nome="form.nome"
        :paradas="form.paradas"
        @edit-step="goToStep"
        @remove-stop="onRemoveStop"
      />
    </div>

    <div class="itineraries-create-drawer__footer">
      <a-button size="large" class="itineraries-create-drawer__btn" @click="onClose">
        Cancelar
      </a-button>
      <a-button
        type="primary"
        size="large"
        class="itineraries-create-drawer__btn itineraries-create-drawer__btn--primary"
        :loading="saving"
        :disabled="isEdit ? !canReachReview : !canPrimary"
        @click="handlePrimary"
      >
        {{ isEdit ? "Concluir" : primaryLabel }}
        <a-icon v-if="!isEdit && currentStep < 2" type="right" />
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
import ItineraryNameStep from '@/modules/Itineraries/components/Create/ItineraryNameStep.vue';
import ItineraryStopsStep from '@/modules/Itineraries/components/Create/ItineraryStopsStep.vue';
import ItineraryReviewStep from '@/modules/Itineraries/components/Create/ItineraryReviewStep.vue';
import { handleApiError } from '@/modules/Itineraries/utils/handleApiError';

export default {
  name: 'ItinerariesCreateDrawer',
  components: {
    ItineraryNameStep,
    ItineraryStopsStep,
    ItineraryReviewStep,
  },
  props: {
    visible: { type: Boolean, default: false },
    draft: { type: Object, default: null },
    mode: { type: String, default: 'create' },
  },
  data() {
    return {
      currentStep: 0,
      saving: false,
      form: {
        id: null,
        codigo: '',
        nome: '',
        descricao: '',
        paradas: [],
        userId: null,
        status: 'draft',
      },
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    drawerTitle() {
      const nome = (this.form.nome || '').trim();
      return nome ? `Roteiro ${nome}` : 'Novo roteiro';
    },
    canReachStops() {
      return !!(this.form.nome || '').trim();
    },
    canReachReview() {
      return this.canReachStops && this.form.paradas.length >= 1;
    },
    canPrimary() {
      if (this.currentStep === 0) return this.canReachStops;
      if (this.currentStep === 1) return this.canReachReview;
      return this.canReachReview;
    },
    primaryLabel() {
      return this.currentStep < 2 ? 'Avançar' : 'Salvar';
    },
  },
  watch: {
    draft: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.form = {
          id: value.id,
          codigo: value.codigo || '',
          nome: value.nome || '',
          descricao: value.descricao || '',
          paradas: value.paradas ? [...value.paradas] : [],
          userId: value.userId,
          status: value.status || 'draft',
        };
        this.currentStep = value.step || 0;
      },
    },
    visible(open) {
      if (!open) {
        this.currentStep = 0;
        this.saving = false;
      }
    },
  },
  methods: {
    onClose() {
      if (this.isEdit) {
        this.$emit('close');
        return;
      }
      this.$confirm({
        title: 'Descartar roteiro?',
        content:
          'O roteiro em criação ainda não foi finalizado e será excluído. Deseja sair mesmo assim?',
        okText: 'Descartar',
        okType: 'danger',
        cancelText: 'Voltar',
        onOk: () => this.deleteAndEmit('close'),
      });
    },
    deleteAndEmit(event) {
      if (!this.form.id) {
        this.$emit(event);
        return Promise.resolve();
      }
      return this.$store
        .dispatch('itineraries/deleteItinerary', { itineraryId: this.form.id })
        .then(() => {
          this.$emit(event);
        })
        .catch((error) => {
          handleApiError(this, error, 'Não foi possível excluir o roteiro');
        });
    },
    goToStep(step) {
      if (step === 1 && !this.canReachStops) return;
      if (step === 2 && !this.canReachReview) return;
      this.currentStep = step;
    },
    onParadasChange(paradas) {
      this.form.paradas = paradas;
    },
    onRemoveStop(id) {
      this.form.paradas = this.form.paradas.filter((p) => p.id !== id);
      if (this.currentStep === 2 && this.form.paradas.length < 1) {
        this.currentStep = 1;
      }
    },
    syncParadas() {
      return this.$store.dispatch('itineraries/syncItineraryPessoas', {
        itineraryId: this.form.id,
        personIds: this.form.paradas.map((p) => p.pessoaId),
      });
    },
    handlePrimary() {
      if (this.isEdit) this.onConcluir();
      else this.onPrimary();
    },
    async onPrimary() {
      if (!this.canPrimary || this.saving) return;
      this.saving = true;
      try {
        if (this.currentStep === 0) {
          await this.$store.dispatch('itineraries/updateItineraryDetails', {
            itineraryId: this.form.id,
            descr: this.form.nome.trim(),
            obs: this.form.descricao || '',
          });
          this.currentStep = 1;
        } else if (this.currentStep === 1) {
          this.currentStep = 2;
        } else {
          await this.syncParadas();
          await this.$store.dispatch('itineraries/submitItinerary', {
            itineraryId: this.form.id,
          });
          this.$emit('created');
        }
      } catch (error) {
        handleApiError(this, error, 'Não foi possível salvar o roteiro');
      } finally {
        this.saving = false;
      }
    },
    async onConcluir() {
      if (!this.canReachReview || this.saving) return;
      this.saving = true;
      try {
        await this.syncParadas();
        this.$emit('saved');
      } catch (error) {
        handleApiError(this, error, 'Não foi possível salvar as paradas');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
@import '~@/shared/styles/variables.less';

.itineraries-create-drawer {
  &__header {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: @color-text-dark;
  }

  &__status {
    font-size: 13px;
    color: @color-text-light;
    font-weight: 400;
  }

  &__steps {
    margin-bottom: 24px;

    /deep/ .ant-steps-item-process .ant-steps-item-icon {
      background: @color-primary;
      border-color: @color-primary;
    }

    /deep/ .ant-steps-item-finish .ant-steps-item-icon {
      border-color: @color-primary;

      .ant-steps-icon {
        color: @color-primary;
      }
    }

    /deep/ .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after {
      background-color: @color-primary;
    }
  }

  &__body {
    padding-bottom: 72px;
  }

  &__footer {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 100%;
    border-top: 1px solid #f0f0f0;
    padding: 16px 24px;
    background: #fff;
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }

  &__step-link {
    color: inherit;

    &.is-disabled {
      pointer-events: none;
      opacity: 0.55;
    }
  }
}
</style>

<style lang="less">
@import '~@/modules/Itineraries/styles/create-form.less';

.itineraries-create-drawer {
  .ant-drawer-body {
    padding-bottom: 88px;
  }

  .ant-drawer-title {
    width: 100%;
  }
}
</style>
